import React, { useEffect, useState } from "react";
import {
  Gavel,
  Loader2,
  FileDown,
  Plus,
  Send,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Ban,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { formatBRL, formatDateTime } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/lib/auth";

const statusMeta = {
  ABERTA: { label: "ABERTA", icon: Clock, cls: "border-amber-500/40 text-amber-400 bg-amber-500/10" },
  DEFENDIDA: { label: "DEFENDIDA", icon: Send, cls: "border-sky-500/40 text-sky-400 bg-sky-500/10" },
  GANHOU: { label: "GANHOU", icon: CheckCircle2, cls: "border-primary/40 text-primary bg-primary/10" },
  PERDEU: { label: "PERDEU", icon: XCircle, cls: "border-red-500/40 text-red-400 bg-red-500/10" },
  CANCELADA: { label: "CANCELADA", icon: Ban, cls: "border-muted text-muted-foreground" },
};

function downloadDefensePdf(id) {
  const token = localStorage.getItem("ml_token");
  fetch(`${API}/disputes/${id}/defense.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(async (r) => {
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `defesa_med_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Relatório de defesa baixado");
    })
    .catch(() => toast.error("Erro ao baixar relatório"));
}

function NewDisputeDialog({ open, onOpenChange, onCreated }) {
  const [paidCharges, setPaidCharges] = useState([]);
  const [chargeId, setChargeId] = useState("");
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("FRAUDE");
  const [bank, setBank] = useState("");
  const [deadline, setDeadline] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/charges", { params: { status_filter: "PAID", limit: 200 } })
      .then((res) => setPaidCharges(res.data))
      .catch(() => toast.error("Erro ao listar cobranças"));
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!chargeId || !reason) {
      toast.error("Preencha a cobrança e o motivo");
      return;
    }
    setSubmitting(true);
    try {
      const body = { charge_id: chargeId, reason, category, bank };
      if (deadline) body.deadline_at = new Date(deadline).toISOString();
      await api.post("/disputes", body);
      toast.success("Contestação registrada");
      onCreated?.();
      onOpenChange(false);
      setChargeId("");
      setReason("");
      setBank("");
      setDeadline("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao registrar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Registrar contestação PIX MED</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Cobrança relacionada</Label>
            <Select value={chargeId} onValueChange={setChargeId}>
              <SelectTrigger className="bg-secondary/40" data-testid="dispute-charge-select">
                <SelectValue placeholder="Selecione a venda contestada..." />
              </SelectTrigger>
              <SelectContent>
                {paidCharges.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {formatBRL(c.value)} — {c.payer?.name || c.customer_name || c.id.slice(0, 8)} · {formatDateTime(c.paid_at)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Categoria</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="bg-secondary/40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="FRAUDE">Fraude alegada</SelectItem>
                <SelectItem value="NAO_RECONHECIDA">Não reconhecida</SelectItem>
                <SelectItem value="COERCAO">Alegação de coerção</SelectItem>
                <SelectItem value="GOLPE">Golpe</SelectItem>
                <SelectItem value="OUTRO">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider">Motivo alegado pelo cliente</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Ex: Cliente alega não reconhecer a transação..."
              className="bg-secondary/40"
              data-testid="dispute-reason-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">Banco contestante</Label>
              <Input
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                placeholder="Nubank, Itaú..."
                className="bg-secondary/40"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">Prazo de defesa</Label>
              <Input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="bg-secondary/40"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="dispute-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DefendDialog({ dispute, open, onOpenChange, onDefended }) {
  const [args, setArgs] = useState("");
  const [evidence, setEvidence] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (dispute) {
      setArgs(dispute.defense_arguments || "");
      setEvidence((dispute.evidence || []).join("\n"));
    }
  }, [dispute]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/disputes/${dispute.id}/defend`, {
        defense_arguments: args,
        evidence: evidence.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Defesa enviada. Baixando relatório PDF...");
      downloadDefensePdf(dispute.id);
      onDefended?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao enviar defesa");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Defender contestação & gerar relatório</DialogTitle>
        </DialogHeader>
        {dispute && (
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-lg border border-border p-3 bg-secondary/30 text-sm">
              <p className="text-xs uppercase text-muted-foreground mb-1">Motivo alegado</p>
              <p>{dispute.reason}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">
                Argumentos da defesa
              </Label>
              <Textarea
                value={args}
                onChange={(e) => setArgs(e.target.value)}
                rows={7}
                placeholder="Descreva detalhadamente a prestação do serviço, comprovações de entrega, comunicações com o cliente, etc."
                className="bg-secondary/40"
                data-testid="defense-args-input"
              />
              <p className="text-xs text-muted-foreground">
                Deixe em branco para usar o texto padrão de defesa (serviço prestado, pagamento voluntário, sem fraude).
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider">Evidências (uma por linha)</Label>
              <Textarea
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                rows={4}
                placeholder="Ex:&#10;Recibo de serviço nº 2026-000001&#10;Conversa no WhatsApp em 05/04/2026&#10;Acesso liberado ao material no dia..."
                className="bg-secondary/40 font-mono text-xs"
              />
            </div>
            <DialogFooter>
              <Button
                type="submit"
                disabled={submitting}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="defend-submit-btn"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Enviar defesa & baixar PDF
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Disputes() {
  const { hasPerm } = useAuth();
  const canRespond = hasPerm("respond_disputes");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [newOpen, setNewOpen] = useState(false);
  const [defendOpen, setDefendOpen] = useState(false);
  const [currentDispute, setCurrentDispute] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "ALL") params.status_filter = statusFilter;
      const res = await api.get("/disputes", { params });
      setList(res.data);
    } catch {
      toast.error("Erro ao carregar contestações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [statusFilter]);

  const openDefend = (d) => {
    setCurrentDispute(d);
    setDefendOpen(true);
  };

  const updateStatus = async (d, newStatus) => {
    try {
      await api.put(`/disputes/${d.id}`, { status: newStatus });
      toast.success("Status atualizado");
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const stats = {
    open: list.filter((d) => d.status === "ABERTA").length,
    defended: list.filter((d) => d.status === "DEFENDIDA").length,
    won: list.filter((d) => d.status === "GANHOU").length,
  };

  return (
    <div className="space-y-6" data-testid="disputes-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
            PIX MED
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-bold">Contestações</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Registre devoluções PIX MED contestadas por clientes e gere o relatório completo com a nota para enviar ao banco.
          </p>
        </div>
        {canRespond && (
          <Button
            onClick={() => setNewOpen(true)}
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="new-dispute-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Registrar contestação
          </Button>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-amber-500/5 border-amber-500/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Abertas</p>
            </div>
            <p className="font-display font-bold text-3xl font-mono-num text-amber-400">
              {stats.open}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-sky-500/5 border-sky-500/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Send className="w-4 h-4 text-sky-400" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Defendidas</p>
            </div>
            <p className="font-display font-bold text-3xl font-mono-num text-sky-400">
              {stats.defended}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-primary/5 border-primary/30">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-primary" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Ganhas</p>
            </div>
            <p className="font-display font-bold text-3xl font-mono-num text-primary">
              {stats.won}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <Gavel className="w-4 h-4" />
            Lista de contestações
          </CardTitle>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-secondary/40" data-testid="dispute-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Todos</SelectItem>
              <SelectItem value="ABERTA">Abertas</SelectItem>
              <SelectItem value="DEFENDIDA">Defendidas</SelectItem>
              <SelectItem value="GANHOU">Ganhas</SelectItem>
              <SelectItem value="PERDEU">Perdidas</SelectItem>
              <SelectItem value="CANCELADA">Canceladas</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : list.length === 0 ? (
            <div className="py-16 text-center text-sm text-muted-foreground">
              Nenhuma contestação registrada.
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30 border-border">
                    <TableHead className="text-xs uppercase">Data</TableHead>
                    <TableHead className="text-xs uppercase">Motivo</TableHead>
                    <TableHead className="text-xs uppercase">Categoria</TableHead>
                    <TableHead className="text-xs uppercase">Valor</TableHead>
                    <TableHead className="text-xs uppercase">Prazo</TableHead>
                    <TableHead className="text-xs uppercase">Status</TableHead>
                    <TableHead className="text-xs uppercase text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.map((d) => {
                    const sm = statusMeta[d.status] || statusMeta.ABERTA;
                    const Icon = sm.icon;
                    return (
                      <TableRow key={d.id} className="border-border" data-testid={`dispute-row-${d.id}`}>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(d.created_at)}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{d.reason}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{d.category || "-"}</TableCell>
                        <TableCell className="font-mono-num font-semibold">
                          {formatBRL(d.amount_cents)}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {d.deadline_at ? formatDateTime(d.deadline_at) : "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={sm.cls}>
                            <Icon className="w-3 h-3 mr-1" />
                            {sm.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDefensePdf(d.id)}
                            data-testid={`download-defense-${d.id}`}
                          >
                            <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                          </Button>
                          {canRespond && d.status === "ABERTA" && (
                            <Button
                              size="sm"
                              className="bg-primary text-primary-foreground hover:bg-primary/90"
                              onClick={() => openDefend(d)}
                              data-testid={`defend-${d.id}`}
                            >
                              <Send className="w-3.5 h-3.5 mr-1" /> Defender
                            </Button>
                          )}
                          {canRespond && d.status === "DEFENDIDA" && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(d, "GANHOU")}
                                className="text-primary border-primary/40"
                                data-testid={`mark-won-${d.id}`}
                              >
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                Ganhou
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(d, "PERDEU")}
                                className="text-red-400 border-red-500/40"
                                data-testid={`mark-lost-${d.id}`}
                              >
                                <XCircle className="w-3.5 h-3.5 mr-1" />
                                Perdeu
                              </Button>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <NewDisputeDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
      <DefendDialog
        open={defendOpen}
        onOpenChange={setDefendOpen}
        dispute={currentDispute}
        onDefended={load}
      />
    </div>
  );
}
